import { FileIcon, FileTextIcon, FolderOpenIcon, type LucideIcon } from "lucide-react";
import type { SessionDetail } from "../../../../shared/api.ts";
import { ATTACHMENT_ICONS } from "@/components/session/attachment-chip.tsx";
import { EmptyState } from "@/components/common/states.tsx";
import { Button } from "@/components/ui/button.tsx";
import { useI18n } from "@/i18n/index.tsx";
import { domainOf, openFile } from "@/lib/utils.ts";

type FileRow = { id: string; name: string; bytes: number | null; url: string; icon: LucideIcon; tone: "danger" | "primary"; meta: string };

const DOC_ICONS: Record<"page" | "pdf" | "file", LucideIcon> = {
  page: FileTextIcon,
  pdf: FileTextIcon,
  file: FileIcon,
};

/** The documents the user uploaded and the ones stored from sources. Images are in the Images tab. */
export function collectFiles(detail: SessionDetail): { uploads: FileRow[]; stored: FileRow[] } {
  const uploads: FileRow[] = [];
  const seen = new Set<string>();
  for (const message of detail.messages) {
    if (message.role !== "user") continue;
    for (const attachment of message.attachments) {
      if (attachment.kind === "image" || seen.has(attachment.url)) continue;
      seen.add(attachment.url);
      uploads.push({
        id: attachment.id,
        name: attachment.name,
        bytes: attachment.bytes,
        url: attachment.url,
        icon: ATTACHMENT_ICONS[attachment.kind],
        tone: attachment.kind === "pdf" ? "danger" : "primary",
        meta: attachment.contentType,
      });
    }
  }
  const stored: FileRow[] = [];
  for (const source of detail.sources) {
    const doc = source.document;
    if (!doc?.rawUrl || doc.kind === "image" || seen.has(doc.rawUrl)) continue;
    seen.add(doc.rawUrl);
    stored.push({
      id: source.id,
      name: source.title || domainOf(source.url) || doc.id,
      bytes: doc.bytes,
      url: doc.rawUrl,
      icon: DOC_ICONS[doc.kind],
      tone: doc.kind === "pdf" ? "danger" : "primary",
      meta: domainOf(source.url) || doc.contentType || "",
    });
  }
  return { uploads, stored };
}

function FileList({ title, rows }: { title: string; rows: FileRow[] }) {
  const { t, fmt } = useI18n();
  if (rows.length === 0) return null;
  return (
    <section>
      <h3 className="border-b px-4 py-2 text-[11.5px] font-bold tracking-[0.06em] text-muted-foreground uppercase">{title}</h3>
      <ul className="divide-y">
        {rows.map((row) => {
          const Icon = row.icon;
          return (
            <li key={row.id} className="flex items-center gap-3 px-4 py-2.5">
              <span className="flex size-8 shrink-0 items-center justify-center rounded-lg border bg-card">
                <Icon className={row.tone === "danger" ? "size-4 text-danger" : "size-4 text-primary"} aria-hidden />
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-[13px] font-semibold" title={row.name}>
                  {row.name}
                </p>
                <p className="truncate text-[11.5px] text-muted-foreground">
                  <span className="code-mono">{fmt.bytes(row.bytes)}</span>
                  {row.meta ? ` · ${row.meta}` : ""}
                </p>
              </div>
              <Button variant="outline" size="xs" onClick={() => openFile(row.url)}>
                {t.files.open}
              </Button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

export function FilesTab({ detail }: { detail: SessionDetail }) {
  const { t } = useI18n();
  const { uploads, stored } = collectFiles(detail);
  if (uploads.length === 0 && stored.length === 0) {
    return <EmptyState icon={FolderOpenIcon} text={t.files.empty} className="py-14" />;
  }
  return (
    <div className="pb-4">
      <FileList title={t.files.uploads} rows={uploads} />
      <FileList title={t.files.stored} rows={stored} />
    </div>
  );
}
